import * as React from "react";
import {
  Navigate,
  RouterProvider,
  createHashRouter,
  redirect,
} from "react-router-dom";
import { LegacyApp } from "./pages/LegacyApp";
import { DateRangeDialog } from "./DateRangeDialog";
import { SuccessMessage } from "./SuccessMessage";
import { PINResetPage } from "./PinResetPage";
import {
  LocationConfigurationHolder,
  LocationConfigurationProvider,
} from "./LocationConfiguration";
import { GlobalConfigHolder, GlobalConfigProvider } from "./GlobalConfig";
import { SupportedFunctionalityProvider } from "./SupportedFunctionality";
import { WindowLocationProvider } from "./WindowLocation";
import { WindowVariablesProvider } from "./WindowVariables";
import { StationLocationProvider } from "./StationLocation";
import { AccountHolder, AccountProvider } from "./Account";
import { SyndeticsConfigProvider } from "./SyndeticsConfig";
import { LoginDialog } from "./LoginDialog";
import { Root, rootLoader } from "./Root";
import { MyAccount, myAccountLoader } from "./pages/account/MyAccount";
import { AccountSummaryPage } from "./pages/account/AccountSummaryPage";
import { AccountLoansPage } from "./pages/account/AccountLoansPage";
import { LoginPage } from "./pages/LoginPage";
import {
  LinkedAccountsPage,
  linkedAccountsLoader,
} from "./pages/account/LinkedAccountsPage";
import {
  AccountHoldsPage,
  holdsLoader,
} from "./pages/account/AccountHoldsPage";
import {
  AccountHistoryPage,
  loanHistoryLoader,
} from "./pages/account/AccountHistoryPage";
import { AccountFinesPage } from "./pages/account/AccountFinesPage";
import {
  savedSearchesLoader,
  SavedSearchesPage,
} from "./pages/account/SavedSearchesPage";
import {
  SavedListsPage,
  listLoader,
} from "./pages/account/lists/SavedListPage";
import { AdvancedSearchPage } from "./pages/search/AdvancedSearchPage";
import { BooleanSearchPage } from "./pages/search/BooleanSearchPage";
import {
  ReservesSearchPage,
  reservesLoader,
} from "./pages/search/ReservesSearchPage";
import {
  ReadingLevelSearchPage,
  readingLevelLoader,
} from "./pages/search/ReadingLevelSearchPage";
import { BranchLimitProvider } from "./BranchLimits";
import { BrandingPage } from "./pages/config/BrandingPage";
import { LandingPageConfigPage } from "./pages/config/LandingPageConfigPage";
import {
  TagManagementPage,
  tagsLoader,
} from "./pages/config/TagManagementPage";
import { SearchPage } from "./pages/search/SearchPage";
import {
  CreateAccountPage,
  createAccountPageLoader,
} from "./pages/CreateAccountPage";
import { HomePage } from "./pages/HomePage";
import { EditPINPage } from "./pages/account/EditPINPage";
import { EditPasswordPage } from "./pages/account/EditPasswordPage";
import { EditUsernamePage } from "./pages/account/EditUsernamePage";
import { CreateUsernamePage } from "./pages/account/CreateUsernamePage";
import { EditContactInfoPage } from "./pages/account/EditContactInfoPage";
import { ForgotPasswordDialogProvider } from "./ForgotPasswordDialog";
import { LogoutPage } from "./pages/LogoutPage";
import { FourOhFourPage } from "./pages/FourOhFourPage";
import { ListService } from "./pages/account/ListService";
import { LoginContextProvider } from "./LoginContext";

const router = createHashRouter([
  {
    path: "/",
    element: <Root></Root>,
    loader: rootLoader,
    children: [
      {
        id: "myaccount", // so we can useRouteLoaderData("myaccount") from summary, etc.
        path: "/myaccount",
        element: <MyAccount></MyAccount>,
        loader: myAccountLoader,
        children: [
          {
            path: "summary",
            element: <AccountSummaryPage></AccountSummaryPage>,
          },
          {
            path: "edit-pin",
            loader: async ({ params, request }) => {
              const { publicHostInformation } =
                await GlobalConfigHolder.fetchGlobalConfig();
              const canEditPin =
                publicHostInformation?.pinEditable &&
                publicHostInformation?.usePin;
              if (!canEditPin) {
                return redirect("/myaccount/summary");
              }
              return canEditPin;
            },
            element: <EditPINPage></EditPINPage>,
          },
          {
            path: "edit-password",
            loader: async ({ params, request }) => {
              const user = await AccountHolder.fetchUser();
              if (!!user && !user.usernameSet) {
                return redirect("/myaccount/create-username");
              }
              return user;
            },
            element: <EditPasswordPage></EditPasswordPage>,
          },
          {
            path: "edit-username",
            loader: async ({ params, request }) => {
              const user = await AccountHolder.fetchUser();
              if (!!user && !user.usernameSet) {
                return redirect("/myaccount/create-username");
              }
              return user;
            },
            element: <EditUsernamePage></EditUsernamePage>,
          },
          {
            path: "create-username",
            loader: async ({ params, request }) => {
              const user = await AccountHolder.fetchUser();
              if (!!user && user.usernameSet === true) {
                return redirect("/myaccount/edit-username");
              }
              return user;
            },
            element: <CreateUsernamePage></CreateUsernamePage>,
          },
          {
            path: "edit-contact-info",
            loader: async () => {
              const { publicHostInformation, globalSettings } =
                await GlobalConfigHolder.fetchGlobalConfig();
              const canEditContactInfo =
                publicHostInformation?.emailEditable ||
                publicHostInformation?.addressEditable ||
                publicHostInformation?.phoneNumberEditable;
              if (
                globalSettings?.hidePersonalInformation ||
                !canEditContactInfo
              ) {
                return redirect("/myaccount/summary");
              }
              return canEditContactInfo;
            },
            element: <EditContactInfoPage></EditContactInfoPage>,
          },
          {
            path: "linked",
            loader: linkedAccountsLoader,
            element: <LinkedAccountsPage></LinkedAccountsPage>,
          },
          {
            path: "loans",
            element: <AccountLoansPage></AccountLoansPage>,
          },
          {
            path: "holds",
            loader: holdsLoader,
            element: <AccountHoldsPage></AccountHoldsPage>,
          },
          {
            path: "fines",
            element: <AccountFinesPage></AccountFinesPage>,
          },
          {
            path: "history",
            loader: loanHistoryLoader,
            element: <AccountHistoryPage></AccountHistoryPage>,
          },
          {
            path: "lists",
            id: "lists",
            element: null,
            children: [
              {
                path: ":listId",
                loader: listLoader,
                element: <SavedListsPage></SavedListsPage>,
              },
              {
                path: "*",
                loader: async () => {
                  const path = await ListService.getDefaultListPath();
                  return redirect(path);
                },
                element: null,
              },
              {
                path: "",
                loader: async () => {
                  const path = await ListService.getDefaultListPath();
                  return redirect(path);
                },
                element: null,
              },
            ],
          },
          {
            path: "searches",
            loader: savedSearchesLoader,
            element: <SavedSearchesPage></SavedSearchesPage>,
          },
        ],
      },
      {
        path: "/config",
        id: "config",
        element: <MyAccount></MyAccount>,
        loader: myAccountLoader,
        children: [
          {
            path: "landing-page",
            element: <LandingPageConfigPage></LandingPageConfigPage>,
          },
          {
            path: "tags",
            loader: tagsLoader,
            element: <TagManagementPage></TagManagementPage>,
          },
          {
            path: "branding",
            element: <BrandingPage></BrandingPage>,
          }
        ],
      },
      {
        path: "/advanced-search",
        element: <AdvancedSearchPage></AdvancedSearchPage>,
        children: [
          {
            path: "boolean",
            element: <BooleanSearchPage></BooleanSearchPage>,
          },
          {
            path: "reserves",
            loader: reservesLoader,
            element: <ReservesSearchPage></ReservesSearchPage>,
          },
          {
            path: "reading-levels",
            loader: readingLevelLoader,
            element: <ReadingLevelSearchPage></ReadingLevelSearchPage>,
          },
          {
            path: "",
            element: <Navigate to="boolean" replace={true} />,
          },
        ],
      },
      {
        path: "/create-account",
        loader: createAccountPageLoader,
        element: <CreateAccountPage></CreateAccountPage>,
      },
      {
        path: "/login",
        element: <LoginPage></LoginPage>,
      },
      {
        path: "/logout", // this path could be a problem as it overlaps with API
        element: <LogoutPage></LogoutPage>,
      },
      {
        path: "/search",
        element: <SearchPage></SearchPage>,
      },
      {
        path: "/",
        element: <HomePage></HomePage>,
      },
      {
        path: "/404",
        element: <FourOhFourPage></FourOhFourPage>,
      },
      {
        path: "*",
        loader: async ({ params, request }) => {
          const hash = window.location.hash;

          if (hash) {
            const params = new URLSearchParams(hash.slice(1)); // Remove '#' and parse
            const section = params.get("section");
            const page = params.get("page");
            const tab = params.get("tab");

            // Redirect mappings for old URLs
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "accountSummary"
            ) {
              return redirect("/myaccount/summary");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "contactInformation"
            ) {
              return redirect("/myaccount/edit-contact-info");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "linkedAccountsTab"
            ) {
              return redirect("/myaccount/linked");
            }

            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "updatePin"
            ) {
              return redirect("/myaccount/edit-pin");
            }
            if (
              section === "createezlogin" ||
              (section === "myaccount" &&
                page === "accountSettings" &&
                tab === "updateEZLogin")
            ) {
              return redirect("/myaccount/create-username");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "updateEZUsername"
            ) {
              return redirect("/myaccount/edit-username");
            }
            if (
              section === "myaccount" &&
              page === "accountSettings" &&
              tab === "updateEZPassword"
            ) {
              return redirect("/myaccount/edit-password");
            }
            if (section === "accountsettings") {
              return redirect("/myaccount/summary");
            }
            if (
              section === "myaccount" &&
              (!page || page === "accountSettings") &&
              !tab
            ) {
              return redirect("/myaccount/summary");
            }
            if (section === "myaccount" && page === "accountActivity" && !tab) {
              return redirect("/myaccount/loans");
            }
            if (
              section === "loans" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "loans")
            ) {
              return redirect("/myaccount/loans");
            }
            if (
              section === "holds" ||
              section === "requests" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "holds")
            ) {
              return redirect("/myaccount/holds");
            }
            if (
              section === "fines" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "fines")
            ) {
              return redirect("/myaccount/fines");
            }
            if (
              section === "loanhistory" ||
              (section === "myaccount" &&
                page === "accountActivity" &&
                tab === "loanHistory")
            ) {
              return redirect("/myaccount/history");
            }
            if (
              section === "mylists" ||
              (section === "myaccount" && page === "myLists")
            ) {
              if (tab) {
                return redirect(`/myaccount/lists/${tab}`);
              }
              return redirect(`/myaccount/lists`);
            }
            if (
              section === "myaccount" &&
              page === "mySearches" &&
              tab === "mySearches"
            ) {
              return redirect("/myaccount/searches");
            }
            if (
              section === "myaccount" &&
              page === "configuration" &&
              tab === "landingPageConfigTab"
            ) {
              return redirect("/config/landing-page");
            }
            if (
              section === "myaccount" &&
              page === "configuration" &&
              tab === "tagManagementTab"
            ) {
              return redirect("/config/tags");
            }
            if (section === "home") {
              return redirect("/");
            }
            if (section === "advancedsearch" && (!page || page === "boolean")) {
              return redirect("/advanced-search/boolean");
            }
            if (section === "advancedsearch" && page === "reserves") {
              return redirect("/advanced-search/reserves");
            }
            if (section === "advancedsearch" && page === "readingLevels") {
              return redirect("/advanced-search/reading-levels");
            }
            if (section === "obrsignup") {
              return redirect("/create-account");
            }
            if (section === "login") {
              return redirect("/login");
            }
          }

          return null;
        },
        element: <LegacyApp></LegacyApp>,
      },
    ],
  },
]);

export const App: React.FC = () => {
  return (
    <>
      <LoginContextProvider>
        <DateRangeDialog></DateRangeDialog>
        <SuccessMessage></SuccessMessage>
        <PINResetPage></PINResetPage>
        <LocationConfigurationProvider>
          <GlobalConfigProvider>
            <SupportedFunctionalityProvider>
              <WindowLocationProvider>
                <WindowVariablesProvider>
                  <StationLocationProvider>
                    <ForgotPasswordDialogProvider>
                        <AccountProvider>
                            <SyndeticsConfigProvider>
                              <BranchLimitProvider>
                                <RouterProvider router={router}></RouterProvider>
                              </BranchLimitProvider>
                            </SyndeticsConfigProvider>
                        </AccountProvider>
                        <LoginDialog isKids={false}></LoginDialog>
                    </ForgotPasswordDialogProvider>
                  </StationLocationProvider>
                </WindowVariablesProvider>
              </WindowLocationProvider>
            </SupportedFunctionalityProvider>
          </GlobalConfigProvider>
        </LocationConfigurationProvider>
      </LoginContextProvider>
    </>
  );
};
